import { Add } from '@mui/icons-material';
import {
	Button,
	CircularProgress,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableFooter,
	TableHead,
	TableRow,
	Typography,
	useTheme,
} from '@mui/material';
import MainCard from '../../components/MainCard';
import { getNightOrDayPalette } from '../../utils/getNightOrDayPalette';
import { ReactNode } from 'react';

type TableHeaderData = {
	text: string | JSX.Element;
};

type WithTitle = Readonly<{
	variant?: 'withTitle';
	title: string | JSX.Element;
	totalCount: number;
	action: () => void;
	actionTitle: string | JSX.Element;
}>;

type WithTitleWithoutAction = Readonly<{
	variant?: 'withTitleWithoutAction';
	title: string | JSX.Element;
	totalCount: number;
}>;

type WithoutTitle = Readonly<{
	variant?: 'withoutTitle';
}>;

type Props = {
	headerData: TableHeaderData[];
	tableRows: (JSX.Element | null)[];
	isLoading: boolean;
	emptyState?: ReactNode;
	tableFooter?: ReactNode;
} & (WithTitle | WithoutTitle | WithTitleWithoutAction);

const CustomTable = (props: Props): React.ReactElement | null => {
	const theme = useTheme();
	const hasTitleSection = props.variant === 'withTitle' || props.variant === 'withTitleWithoutAction';
	const hasTitleActions = props.variant === 'withTitle';

	if (props.isLoading) {
		return <CircularProgress />;
	}

	return (
		<MainCard
			headerSX={{ p: 2, border: `none` }}
			content={false}
			title={
				hasTitleSection ? (
					<Stack direction={'row'} alignItems={'center'} gap={0.5}>
						<Typography variant={'h5'} fontWeight={theme.typography.fontWeightMedium}>
							{props.title}
						</Typography>
						{/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
						<Typography color={theme.palette[getNightOrDayPalette(theme)][500]}>({props.totalCount})</Typography>
					</Stack>
				) : null
			}
			secondary={
				hasTitleActions ? (
					<Button startIcon={<Add />} variant={'contained'} onClick={props.action}>
						{props.actionTitle}
					</Button>
				) : null
			}
		>
			<TableContainer>
				<Table size={'small'}>
					<TableHead>
						<TableRow>
							{props.headerData.map((header) => (
								<TableCell
									sx={{
										'&.MuiTableCell-root': {
											backgroundColor: (theme) => theme.palette.background.default,
											borderBottom: (theme) => `1px solid  ${theme.palette.divider}`,
										},
									}}
								>
									<Typography variant={'button'} color={theme.palette[getNightOrDayPalette(theme)][500]}>
										{header.text}
									</Typography>
								</TableCell>
							))}
						</TableRow>
					</TableHead>
					<TableBody sx={{ '.MuiTableRow-root:last-child': { '.MuiTableCell-root': { borderBottom: 'none' } } }}>
						{props.tableRows.length === 0 ? props.emptyState : props.tableRows}
					</TableBody>
					{props.tableFooter && <TableFooter>{props.tableFooter}</TableFooter>}
				</Table>
			</TableContainer>
		</MainCard>
	);
};

export default CustomTable;
